import React, { useCallback, useEffect, useState } from "react";
import { apiUrl } from "config/api";
import { getAccessToken } from "utils/auth";

// Data-quality workbook: download the worklists, upload them filled in, preview, apply.
// Backend: app/api/routes/admin_data_quality.py

const authHeaders = () => {
  const token = getAccessToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const btn = (primary = false, disabled = false) => ({
  padding: "10px 14px",
  borderRadius: 12,
  border: primary ? "none" : "1px solid #cbd5e1",
  background: disabled ? "#e2e8f0" : primary ? "#1f3864" : "#fff",
  color: disabled ? "#94a3b8" : primary ? "#fff" : "#1e293b",
  fontWeight: 600,
  cursor: disabled ? "not-allowed" : "pointer",
});

// Same look as Sinow's .admin-card, via CSS variables with fallbacks, so the card also
// renders on branches without admin.css. Explicit text color: the admin page sets a light
// text color for its dark glass cards, which would be unreadable on this light card.
const card = {
  background: "var(--au-surface, #fff)",
  color: "var(--au-text, #1e293b)",
  border: "1px solid var(--au-border, #e2e8f0)",
  borderRadius: 14,
  padding: 16,
};
const cardTitle = {
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: "0.07em",
  color: "var(--au-faint, #64748b)",
  fontWeight: 600,
  marginBottom: 12,
};

const th = { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid #e2e8f0", fontSize: 13 };
const td = { padding: "6px 8px", borderBottom: "1px solid #f1f5f9", fontSize: 13, verticalAlign: "top" };

function ResultTable({ tx, result }) {
  const sheets = (result?.sheets || []).filter((s) => s.rows_with_input > 0 || s.errors.length > 0);
  if (!sheets.length) {
    return <p style={{ marginTop: 12 }}>{tx("No filled rows found.", "Keine ausgefüllten Zeilen gefunden.", "未找到已填写的行。")}</p>;
  }
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 12 }}>
      <thead>
        <tr>
          <th style={th}>{tx("Sheet", "Blatt", "工作表")}</th>
          <th style={th}>{tx("Type", "Art", "类型")}</th>
          <th style={th}>{tx("Filled rows", "Ausgefüllt", "已填写")}</th>
          <th style={th}>{result.dry_run ? tx("Would apply", "Würde übernehmen", "将应用") : tx("Applied", "Übernommen", "已应用")}</th>
          <th style={th}>{tx("Recorded as decision", "Als Entscheidung", "记录为决定")}</th>
          <th style={th}>{tx("Errors", "Fehler", "错误")}</th>
        </tr>
      </thead>
      <tbody>
        {sheets.map((s) => (
          <tr key={s.sheet}>
            <td style={td}>{s.sheet}</td>
            <td style={td}>{s.mode}</td>
            <td style={td}>{s.rows_with_input}</td>
            <td style={td}>{s.applied}</td>
            <td style={td}>{s.recorded}</td>
            <td style={{ ...td, color: s.errors.length ? "#b91c1c" : undefined }}>
              {s.errors.length === 0
                ? "—"
                : s.errors.slice(0, 5).map((e) => (
                    <div key={`${s.sheet}-${e.row}`}>
                      {tx("Row", "Zeile", "行")} {e.row}: {e.message}
                    </div>
                  ))}
              {s.errors.length > 5 && <div>… +{s.errors.length - 5}</div>}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function DataQualitySection({ tx }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [preview, setPreview] = useState(null);
  const [applied, setApplied] = useState(null);
  const [status, setStatus] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await fetch(apiUrl("/api/admin/data/quality/status"), { credentials: "include", headers: authHeaders() });
      if (res.ok) setStatus(await res.json());
    } catch {
      /* status is informational only */
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Poll while the price history is being rewritten in the background.
  useEffect(() => {
    if (!status?.history?.history_pending) return undefined;
    const t = setTimeout(loadStatus, 10000);
    return () => clearTimeout(t);
  }, [status, loadStatus]);

  const download = async () => {
    setBusy("download");
    setError("");
    try {
      const res = await fetch(apiUrl("/api/admin/data/quality/workbook/export"), { credentials: "include", headers: authHeaders() });
      if (!res.ok) throw new Error(tx("Download failed", "Download fehlgeschlagen", "下载失败"));
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      const name = (cd.match(/filename="([^"]+)"/) || [])[1] || "datenqualitaet.xlsx";
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const upload = async (apply) => {
    if (!file) return;
    setBusy(apply ? "apply" : "preview");
    setError("");
    try {
      const body = new FormData();
      body.append("file", file);
      const res = await fetch(apiUrl(`/api/admin/data/quality/workbook/import?apply=${apply ? "true" : "false"}`), {
        method: "POST",
        credentials: "include",
        headers: authHeaders(),
        body,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || tx("Upload failed", "Upload fehlgeschlagen", "上传失败"));
      if (apply) {
        setApplied(data);
        setPreview(null);
        setFile(null);
        loadStatus();
      } else {
        setPreview(data);
        setApplied(null);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy("");
    }
  };

  const hist = status?.history || {};
  const histTotal = (hist.history_pending || 0) + (hist.history_done || 0) + (hist.history_skipped || 0);
  const previewErrors = (preview?.sheets || []).reduce((n, s) => n + s.errors.length, 0);

  return (
    <div className="col-12">
      <div className="admin-card" style={card}>
        <div className="admin-card__title" style={cardTitle}>{tx("Data quality", "Datenqualität", "数据质量")}</div>
        <p style={{ marginTop: 4 }}>
          {tx(
            "Download the workbook, fill in the yellow columns in Excel, upload it again. You first get a preview; nothing changes until you click Apply.",
            "Arbeitsmappe herunterladen, in Excel die gelben Spalten ausfüllen und wieder hochladen. Zuerst kommt eine Vorschau, geändert wird erst mit „Übernehmen“.",
            "下载工作簿，在 Excel 中填写黄色列后重新上传。先显示预览，点击“应用”后才会更改。"
          )}
        </p>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center", marginTop: 12 }}>
          <button type="button" style={btn(false, busy === "download")} disabled={!!busy} onClick={download}>
            {busy === "download" ? tx("Preparing…", "Wird erstellt…", "生成中…") : tx("1. Download workbook", "1. Arbeitsmappe herunterladen", "1. 下载工作簿")}
          </button>
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => {
              setFile(e.target.files?.[0] || null);
              setPreview(null);
              setApplied(null);
            }}
          />
          <button type="button" style={btn(false, !file || !!busy)} disabled={!file || !!busy} onClick={() => upload(false)}>
            {busy === "preview" ? tx("Checking…", "Wird geprüft…", "检查中…") : tx("2. Check (preview)", "2. Prüfen (Vorschau)", "2. 检查（预览）")}
          </button>
          <button type="button" style={btn(true, !preview || !!busy)} disabled={!preview || !!busy} onClick={() => upload(true)}>
            {busy === "apply" ? tx("Applying…", "Wird übernommen…", "应用中…") : tx("3. Apply", "3. Übernehmen", "3. 应用")}
          </button>
        </div>

        {error && <p style={{ color: "#b91c1c", marginTop: 12 }}>{error}</p>}

        {preview && (
          <div style={{ marginTop: 16 }}>
            <strong>{tx("Preview – nothing has been changed yet", "Vorschau – noch nichts geändert", "预览 – 尚未更改")}</strong>
            {previewErrors > 0 && (
              <p style={{ color: "#b45309", margin: "6px 0 0" }}>
                {tx(
                  "Rows with errors are skipped when applying. Fix them in Excel and upload again, or apply the rest now.",
                  "Zeilen mit Fehlern werden beim Übernehmen übersprungen. In Excel korrigieren und neu hochladen, oder den Rest jetzt übernehmen.",
                  "有错误的行在应用时会被跳过。"
                )}
              </p>
            )}
            {preview.history_materials > 0 && (
              <p style={{ margin: "6px 0 0" }}>
                {tx(
                  `Currency/unit changes affect ${preview.history_materials} materials; their stored price history is updated in the background.`,
                  `Währungs-/Einheiten-Änderungen betreffen ${preview.history_materials} Materialien; deren gespeicherte Preis-Historie wird im Hintergrund angepasst.`,
                  `货币/单位更改影响 ${preview.history_materials} 个材料，其历史价格将在后台更新。`
                )}
              </p>
            )}
            {preview.unknown_sheets?.length > 0 && (
              <p style={{ color: "#b45309", margin: "6px 0 0" }}>
                {tx("Ignored sheets", "Ignorierte Blätter", "忽略的工作表")}: {preview.unknown_sheets.join(", ")}
              </p>
            )}
            <ResultTable tx={tx} result={preview} />
          </div>
        )}

        {applied && (
          <div style={{ marginTop: 16 }}>
            <strong style={{ color: "#15803d" }}>
              {tx("Applied", "Übernommen", "已应用")} (Import {applied.import_id})
            </strong>
            <ResultTable tx={tx} result={applied} />
          </div>
        )}
      </div>

      {status && (
        <div className="admin-card" style={{ ...card, marginTop: 16 }}>
          <div className="admin-card__title" style={cardTitle}>{tx("Status", "Stand", "状态")}</div>
          {histTotal > 0 && (
            <p>
              {tx("Price history update", "Preis-Historie anpassen", "历史价格更新")}: {hist.history_done || 0} / {histTotal}{" "}
              {tx("materials done", "Materialien fertig", "个材料完成")}
              {hist.history_skipped ? ` · ${hist.history_skipped} ${tx("skipped (mixed series)", "übersprungen (gemischte Reihen)", "跳过（混合序列）")}` : ""}
              {hist.history_pending ? ` · ${tx("running…", "läuft…", "进行中…")}` : ""}
            </p>
          )}
          <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
            <div style={{ minWidth: 280 }}>
              <strong>{tx("Open decisions for development", "Offene Entscheidungen für die Umsetzung", "待开发处理的决定")}</strong>
              {(status.recorded_by_sheet || []).length === 0 ? (
                <p>—</p>
              ) : (
                <table style={{ borderCollapse: "collapse", marginTop: 6 }}>
                  <tbody>
                    {status.recorded_by_sheet.map((r) => (
                      <tr key={r.sheet}>
                        <td style={td}>{r.sheet}</td>
                        <td style={td}>{r.anzahl}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div style={{ minWidth: 280 }}>
              <strong>{tx("Recent imports", "Letzte Importe", "最近导入")}</strong>
              {(status.imports || []).length === 0 ? (
                <p>—</p>
              ) : (
                <table style={{ borderCollapse: "collapse", marginTop: 6 }}>
                  <tbody>
                    {status.imports.map((i) => (
                      <tr key={i.import_id}>
                        <td style={td}>{new Date(i.at).toLocaleString()}</td>
                        <td style={td}>{i.imported_by}</td>
                        <td style={td}>
                          {i.applied} {tx("applied", "übernommen", "已应用")}, {i.recorded} {tx("decisions", "Entscheidungen", "决定")}
                          {i.errors ? `, ${i.errors} ${tx("errors", "Fehler", "错误")}` : ""}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
